import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LoadScreen from '../../../components/load-screen';
import ConsignmentForm from '../../../components/reporting/set-turns/ConsignmentForm';
import SetTurnsTable from '../../../components/reporting/set-turns/SetTurnsTable';

import { getHospitals } from '../../../actions/hospitalsActions';
import { getKits } from '../../../actions/kitsActions';
import { getConsignedSets, getSetLogs, getLabels } from '../../../actions/setsActions';

import useLoading from '../../../hooks/useLoading';

import { setLogTypes } from '../../../constants/setTurnsReporting';

const SET_LOG_FETCH_CONCURRENCY = 8;

const mapWithConcurrency = async (items, concurrency, iteratee) => {
  const result = [];

  for (let index = 0; index < items.length; index += concurrency) {
    const chunk = items.slice(index, index + concurrency);
    const chunkResult = await Promise.all(chunk.map(iteratee));
    result.push(...chunkResult);
  }

  return result;
};

const ConsignmentTurnsReportingPage = () => {
  const dispatch = useDispatch();
  const hospitals = useSelector((state) => state?.hospitals?.list);
  const labels = useSelector((state) => state?.sets?.labels);
  const kits = useSelector((state) => state?.kits?.list);
  const currentTenant = useSelector((state) => state?.tenant?.currentTenant);

  const { loading: fetching, startLoading, stopLoading } = useLoading(false);

  const [selectedHospitals, setSelectedHospitals] = useState([]);
  const [target, setTarget] = useState(5);
  const [range, setRange] = useState(3);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [sets, setSets] = useState([]);

  useEffect(() => {
    onLoad().catch((err) => console.error(err));
  }, []);

  const onLoad = async () => {
    startLoading()
    try {
      const [consignedSets] = await Promise.all([
        dispatch(getConsignedSets(currentTenant.id, false)),
        dispatch(getHospitals()),
        dispatch(getLabels()),
        dispatch(getKits()),
      ]);
      setSets(consignedSets.filter((set) => !!set.active));
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      const selectedHospitalIds = new Set(selectedHospitals);
      const kitsById = new Map(kits?.map((kit) => [kit.id, kit]) || []);
      const hospitalsById = new Map(hospitals?.map((hospital) => [hospital.id, hospital]) || []);
      const list = sets?.filter((item) => item.consignment && selectedHospitalIds.has(item?.consignment?.hospital)) || [];

      const result = await mapWithConcurrency(list, SET_LOG_FETCH_CONCURRENCY, async (set) => {
        const logs = await dispatch(getSetLogs(set.id, range));
        const turns = logs?.filter((log) => log.type === setLogTypes.SET_TURNS)?.length || 0;
        const average = Math.round(turns / range);
        const performance = average >= target;

        const kit = kitsById.get(set.kit);
        const kitId = kit?.kitId || '';
        const kitName = kit?.name || '';
        const hospital = hospitalsById.get(set?.consignment?.hospital);

        return {
          id: set.id,
          hospitalName: hospital?.name || '',
          kitId,
          kitName,
          setId: set.id,
          setNumber: set.number,
          labels: set.labels || [],
          turns,
          average,
          performance
        };
      });

      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const hospitalsList = useMemo(() => {
    const consignedHospitalIds = new Set((sets || []).filter((set) => set?.consignment).map((set) => set.consignment.hospital));
    return hospitals?.filter((hospital) => consignedHospitalIds.has(hospital.id));
  }, [sets, hospitals])

  return (
    <div className='page-container reporting-page__container'>
      {fetching && <LoadScreen />}
      <div className='page-title'>Consignment Turns Report</div>
      <div className='reporting-page__body'>
        <ConsignmentForm
          hospitals={hospitalsList}
          selectedHospitals={selectedHospitals}
          setSelectedHospitals={setSelectedHospitals}
          target={target}
          setTarget={setTarget}
          range={range}
          setRange={setRange}
          onSubmit={onSubmit}
          loading={loading}
        />

        {data && !!data.length && (
          <SetTurnsTable
            data={data}
            labels={labels}
          />
        )}
      </div>
    </div>
  );
};

export default ConsignmentTurnsReportingPage;
